<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input } from 'ant-design-vue'
  import { SearchOutlined, WarningFilled, PlusOutlined } from '@ant-design/icons-vue'
  import type { PostProcessingOutputItem } from '../../models/PostProcessingOutputItem'
  import { ListPlus } from 'lucide-vue-next'

  const props = defineProps<{
    visible: boolean
    postProcessingOutputs: Array<PostProcessingOutputItem>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'postProcessingOutput-selected': [items: Array<PostProcessingOutputItem>]
  }>()

  const availablePostProcessingOutputs = ref<PostProcessingOutputItem[]>([])
  const filteredPostProcessingOutputs = ref<PostProcessingOutputItem[]>([])
  const loadingPostProcessingOutputs = ref<boolean>(false)
  const hasErrorLoadingPostProcessingOutputs = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group outputs by `type` (e.g., table, plot)
  const groupedPostProcessingOutputs = ref<Record<string, PostProcessingOutputItem[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredPostProcessingOutputs.value = availablePostProcessingOutputs.value.filter(
        (item: PostProcessingOutputItem) => {
          return (
            (item.title && item.title.toLowerCase().includes(search)) ||
            (item.sourceFileName && item.sourceFileName.toLowerCase().includes(search)) ||
            (item.source && item.source.toLowerCase().includes(search))
          )
        }
      )
    } else {
      filteredPostProcessingOutputs.value = [...availablePostProcessingOutputs.value]
    }
    groupPostProcessingOutputsByType()
  }

  const groupPostProcessingOutputsByType = () => {
    const grouped: Record<string, PostProcessingOutputItem[]> = {}
    filteredPostProcessingOutputs.value.forEach((item) => {
      const key = (item.type as string) ?? 'unknown'
      if (!grouped[key]) {
        grouped[key] = []
      }
      grouped[key].push(item)
    })
    groupedPostProcessingOutputs.value = grouped
  }

  const selectPostProcessingOutput = (item: PostProcessingOutputItem) => {
    emit('postProcessingOutput-selected', [item])
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailablePostProcessingOutputs()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailablePostProcessingOutputs()
      nextTick(() => {
        searchText.value = ''
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailablePostProcessingOutputs = () => {
    availablePostProcessingOutputs.value = props.postProcessingOutputs || []
    filteredPostProcessingOutputs.value = props.postProcessingOutputs || []
    groupPostProcessingOutputsByType()
  }

  const addAllFilteredItems = () => {
    const grouped = groupedPostProcessingOutputs.value || {}
    const combined: PostProcessingOutputItem[] = Object.keys(grouped).reduce(
      (acc: PostProcessingOutputItem[], key: string) => acc.concat(grouped[key] || []),
      []
    )
    emit('postProcessingOutput-selected', combined)
  }

  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.postProcessingOutputs,
    (newItems) => {
      availablePostProcessingOutputs.value = [...(newItems || [])]
      filteredPostProcessingOutputs.value = [...(newItems || [])]
      groupPostProcessingOutputsByType()
    }
  )

  defineExpose({
    availablePostProcessingOutputs
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Post-Processing Output"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="postProcessingOutput-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search outputs by title, file name, or source..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>

        <a-tooltip placement="top">
          <template #title>
            {{ `Add all ${filteredPostProcessingOutputs?.length ?? 0} filtered items` }}
          </template>
          <Button
            type="primary"
            shape="circle"
            size="large"
            :disabled="!filteredPostProcessingOutputs?.length"
            class="ml-5 flex items-center justify-center"
            @click="addAllFilteredItems"
          >
            <ListPlus class="text-base" style="line-height: 1; margin: 0" />
          </Button>
        </a-tooltip>
      </div>

      <!-- Loading State -->
      <div v-if="loadingPostProcessingOutputs" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available outputs...</p>
      </div>

      <!-- Outputs List -->
      <div v-else id="postProcessingOutputsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingPostProcessingOutputs"
          v-for="(outputs, type) in groupedPostProcessingOutputs"
          :key="type"
          class="mb-6"
        >
          <template v-if="outputs.length > 0">
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ type }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ outputs.length }} {{ outputs.length === 1 ? 'output' : 'outputs' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="output in outputs"
                :key="output.id"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectPostProcessingOutput(output)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">
                        {{ output.id }}
                      </h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">
                        {{ `Source: ${output.sourceFileName}` }}
                      </p>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingPostProcessingOutputs"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading outputs.
        </div>

        <!-- No Results -->
        <div
          v-if="!hasErrorLoadingPostProcessingOutputs && filteredPostProcessingOutputs.length === 0"
          class="py-8 text-center"
        >
          <p>
            {{
              searchText.trim()
                ? 'No outputs found matching your search.'
                : 'No post-processing outputs available.'
            }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs">
            Try adjusting your search terms or clear the search to browse all outputs.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any output above to add it to your pipeline</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .postProcessingOutput-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #postProcessingOutputsListContainer {
    flex: 1;
  }
  #postProcessingOutputsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #postProcessingOutputsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #postProcessingOutputsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #postProcessingOutputsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
